import { MembersGroupSettings, PreRegistrationGroupSettings, PreRegistrationSiteSettings } from '@cloudsign/shared';
import { t } from '../../lib/i18n';
import { Button, Card, CheckboxField, SelectField, Spinner, TextField } from '../../components/ui';
import { ErrorLine, ErrorPanel, fieldErrors } from '../../components/ErrorPanel';
import { useToastOnSuccess } from '../../components/Toast';
import { SettingsEditor } from '../../components/SettingsEditor';
import { isValidEmail } from '../../components/phase2';
import type { SettingsCardProps } from '../../components/SettingsCards';
import { EmailSection } from './EmailSection';
import { useSettingsDraft } from '../../lib/useSettingsDraft';

type SiteSettings = ReturnType<typeof PreRegistrationSiteSettings.parse>;
type GroupSettings = ReturnType<typeof PreRegistrationGroupSettings.parse>;
type MemberSettings = ReturnType<typeof MembersGroupSettings.parse>;

function SaveFooter({ canEdit, changed, valid, pending, onSave }: { canEdit: boolean; changed: boolean; valid: boolean; pending: boolean; onSave: () => void }) {
  if (!canEdit) return null;
  return (
    <Button variant="primary" disabled={!changed || !valid || pending} onClick={onSave}>
      {t('common.save')}
    </Button>
  );
}

function emailError(v: string | null): string | undefined {
  return v !== null && v.trim() !== '' && !isValidEmail(v) ? t('prereg.settings.emailInvalid') : undefined;
}

/** Site card: what the pre-arrival page shows (directions, map, parking, Wi-Fi, host contact). */
function PreArrivalSiteCard({ scopeId, canEdit }: { scopeId: string; canEdit: boolean }) {
  const s = useSettingsDraft(`/sites/${scopeId}/settings`, 'pre-registration', PreRegistrationSiteSettings);
  useToastOnSuccess(s.save.isSuccess, t('common.saved'));
  if (s.q.isError) return <ErrorPanel error={s.q.error} onRetry={() => void s.q.refetch()} />;
  const d: SiteSettings | null = s.draft;
  if (!d) return <Spinner />;
  const set = (patch: Partial<SiteSettings>) => s.setDraft({ ...d, ...patch });
  const errs = fieldErrors(s.save.error);
  const mapBad = d.mapUrl.trim() !== '' && !/^https:\/\//i.test(d.mapUrl.trim());
  const mailBad = d.hostContactEmail.trim() !== '' && !isValidEmail(d.hostContactEmail);
  return (
    <Card
      title={t('prereg.site.title')}
      description={t('prereg.site.description')}
      footer={<SaveFooter canEdit={canEdit} changed={s.changed} valid={s.valid && !mapBad && !mailBad} pending={s.save.isPending} onSave={() => s.save.mutate()} />}
    >
      <div className="space-y-4">
        <ErrorLine error={s.save.error} />
        <div>
          <label htmlFor="prereg-directions" className="mb-1 block text-sm font-medium text-slate-800">
            {t('prereg.site.directions')}
          </label>
          <textarea
            id="prereg-directions"
            rows={3}
            maxLength={2000}
            disabled={!canEdit}
            value={d.directions}
            onChange={(e) => set({ directions: e.target.value })}
            className="block w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm disabled:bg-slate-100"
          />
        </div>
        <TextField
          label={t('prereg.site.mapUrl')}
          hint={t('prereg.site.mapUrlHint')}
          type="url"
          disabled={!canEdit}
          value={d.mapUrl}
          error={mapBad ? t('prereg.site.mapUrlInvalid') : errs['mapUrl']}
          onChange={(e) => set({ mapUrl: e.target.value })}
        />
        <TextField label={t('prereg.site.parking')} disabled={!canEdit} value={d.parking} onChange={(e) => set({ parking: e.target.value })} />
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField label={t('prereg.site.wifiNetwork')} disabled={!canEdit} value={d.wifiNetwork} onChange={(e) => set({ wifiNetwork: e.target.value })} />
          <TextField
            label={t('prereg.site.wifiPassword')}
            hint={t('prereg.site.wifiPasswordHint')}
            autoComplete="off"
            disabled={!canEdit}
            value={d.wifiPassword}
            onChange={(e) => set({ wifiPassword: e.target.value })}
          />
        </div>
        <fieldset className="space-y-4">
          <legend className="text-sm font-medium text-slate-800">{t('prereg.site.hostContact')}</legend>
          <div className="grid gap-4 sm:grid-cols-3">
            <TextField label={t('prereg.site.hostName')} disabled={!canEdit} value={d.hostContactName} onChange={(e) => set({ hostContactName: e.target.value })} />
            <TextField label={t('prereg.site.hostPhone')} type="tel" disabled={!canEdit} value={d.hostContactPhone} onChange={(e) => set({ hostContactPhone: e.target.value })} />
            <TextField
              label={t('prereg.site.hostEmail')}
              type="email"
              disabled={!canEdit}
              value={d.hostContactEmail}
              error={mailBad ? t('prereg.settings.emailInvalid') : undefined}
              onChange={(e) => set({ hostContactEmail: e.target.value })}
            />
          </div>
        </fieldset>
      </div>
    </Card>
  );
}

const CHANNELS = ['email', 'sms', 'push'] as const;

/** Group card: require pre-registration, approval, the expected-visit email, pre-arrival page options and no-show alerts. */
function PreRegistrationGroupCard({ scopeId, canEdit }: { scopeId: string; canEdit: boolean }) {
  const s = useSettingsDraft(`/groups/${scopeId}/settings`, 'pre-registration', PreRegistrationGroupSettings);
  useToastOnSuccess(s.save.isSuccess, t('common.saved'));
  if (s.q.isError) return <ErrorPanel error={s.q.error} onRetry={() => void s.q.refetch()} />;
  const d: GroupSettings | null = s.draft;
  if (!d) return <Spinner />;
  const set = (patch: Partial<GroupSettings>) => s.setDraft({ ...d, ...patch });
  const pre = d.prearrival;
  const setPre = (patch: Partial<GroupSettings['prearrival']>) => set({ prearrival: { ...pre, ...patch } });
  const noShow = d.noShow;
  const footer = <SaveFooter canEdit={canEdit} changed={s.changed} valid={s.valid} pending={s.save.isPending} onSave={() => s.save.mutate()} />;
  return (
    <div className="space-y-6">
      <Card title={t('prereg.group.title')} description={t('prereg.group.description')} footer={footer}>
        <div className="space-y-4">
          <ErrorLine error={s.save.error} />
          <CheckboxField label={t('prereg.group.required')} hint={t('prereg.group.requiredHint')} disabled={!canEdit} checked={d.required} onChange={(e) => set({ required: e.target.checked })} />
          <CheckboxField label={t('prereg.group.approval')} hint={t('prereg.group.approvalHint')} disabled={!canEdit} checked={d.requireApproval} onChange={(e) => set({ requireApproval: e.target.checked })} />

          <fieldset className="space-y-3 border-t border-slate-200 pt-4">
            <legend className="text-sm font-semibold text-slate-900">{t('prereg.group.prearrival')}</legend>
            <CheckboxField label={t('prereg.group.pa.enabled')} hint={t('prereg.group.pa.enabledHint')} disabled={!canEdit} checked={pre.enabled} onChange={(e) => setPre({ enabled: e.target.checked })} />
            {pre.enabled ? (
              <div className="ml-1 space-y-3 sm:ml-8">
                <CheckboxField label={t('prereg.group.pa.questions')} disabled={!canEdit} checked={pre.askQuestions} onChange={(e) => setPre({ askQuestions: e.target.checked })} />
                <CheckboxField label={t('prereg.group.pa.notices')} disabled={!canEdit} checked={pre.acceptNotices} onChange={(e) => setPre({ acceptNotices: e.target.checked })} />
                <CheckboxField label={t('prereg.group.pa.photo')} disabled={!canEdit} checked={pre.askPhoto} onChange={(e) => setPre({ askPhoto: e.target.checked })} />
                <CheckboxField label={t('prereg.group.pa.car')} disabled={!canEdit} checked={pre.askCarRegistration} onChange={(e) => setPre({ askCarRegistration: e.target.checked })} />
                <TextField label={t('prereg.group.pa.carField')} hint={t('prereg.group.pa.carFieldHint')} disabled={!canEdit} maxLength={80} value={pre.carRegistrationFieldLabel} onChange={(e) => setPre({ carRegistrationFieldLabel: e.target.value })} />
                <CheckboxField label={t('prereg.group.pa.reschedule')} disabled={!canEdit} checked={pre.visitorCanReschedule} onChange={(e) => setPre({ visitorCanReschedule: e.target.checked })} />
                <CheckboxField label={t('prereg.group.pa.cancel')} disabled={!canEdit} checked={pre.visitorCanCancel} onChange={(e) => setPre({ visitorCanCancel: e.target.checked })} />
              </div>
            ) : null}
          </fieldset>

          <fieldset className="space-y-3 border-t border-slate-200 pt-4">
            <legend className="text-sm font-semibold text-slate-900">{t('prereg.group.noShow')}</legend>
            <TextField
              label={t('prereg.group.noShow.after')}
              hint={t('prereg.group.noShow.afterHint')}
              type="number"
              min={0}
              max={1440}
              disabled={!canEdit}
              value={noShow.afterMinutes}
              onChange={(e) => set({ noShow: { ...noShow, afterMinutes: Math.max(0, Math.min(1440, Math.trunc(Number(e.target.value) || 0))) } })}
              className="max-w-xs"
            />
            {noShow.afterMinutes > 0 ? (
              <fieldset className="flex flex-wrap gap-x-6 gap-y-2">
                <legend className="mb-1 text-sm font-medium text-slate-800">{t('prereg.group.noShow.channels')}</legend>
                {CHANNELS.map((c) => (
                  <CheckboxField
                    key={c}
                    label={t(`prereg.channel.${c}`)}
                    disabled={!canEdit}
                    checked={noShow.channels.includes(c)}
                    onChange={(e) => set({ noShow: { ...noShow, channels: e.target.checked ? [...noShow.channels, c] : noShow.channels.filter((x) => x !== c) } })}
                  />
                ))}
              </fieldset>
            ) : null}
          </fieldset>
        </div>
      </Card>

      <Card title={t('prereg.email.title')} description={t('prereg.email.description')} footer={footer}>
        <div className="space-y-4">
          <CheckboxField label={t('prereg.email.enabled')} disabled={!canEdit} checked={d.expectedEmail.enabled} onChange={(e) => set({ expectedEmail: { ...d.expectedEmail, enabled: e.target.checked } })} />
          {d.expectedEmail.enabled ? (
            <EmailSection
              value={d.expectedEmail}
              onChange={(expectedEmail) => set({ expectedEmail })}
              context="preregistration"
              uploadPath="/pre-registration/group-attachment"
              groupId={scopeId}
              showCalendar
              disabled={!canEdit}
            />
          ) : null}
          {emailError(d.expectedEmail.replyTo) ? <p className="text-sm text-red-700">{emailError(d.expectedEmail.replyTo)}</p> : null}
        </div>
      </Card>
    </div>
  );
}

/** Registered for `pre-registration`: the site gets the pre-arrival page card, a group gets its pre-registration and email cards. */
export function PreRegistrationSettingsCard({ scope, scopeId, canEdit }: SettingsCardProps) {
  return scope === 'sites' ? <PreArrivalSiteCard scopeId={scopeId} canEdit={canEdit} /> : <PreRegistrationGroupCard scopeId={scopeId} canEdit={canEdit} />;
}

function MembersGroupCard({ scopeId, canEdit }: { scopeId: string; canEdit: boolean }) {
  const s = useSettingsDraft(`/groups/${scopeId}/settings`, 'members', MembersGroupSettings);
  useToastOnSuccess(s.save.isSuccess, t('common.saved'));
  if (s.q.isError) return <ErrorPanel error={s.q.error} onRetry={() => void s.q.refetch()} />;
  const d: MemberSettings | null = s.draft;
  if (!d) return <Spinner />;
  const set = (patch: Partial<MemberSettings>) => s.setDraft({ ...d, ...patch });
  const ob = d.onboarding;
  const setLife = (patch: Partial<MemberSettings['lifecycle']>) => set({ lifecycle: { ...d.lifecycle, ...patch } });
  const days = (v: string) => Math.max(0, Math.min(3650, Math.trunc(Number(v) || 0)));
  const footer = <SaveFooter canEdit={canEdit} changed={s.changed} valid={s.valid} pending={s.save.isPending} onSave={() => s.save.mutate()} />;
  return (
    <div className="space-y-6">
      <Card title={t('prereg.members.lifecycle')} footer={footer}>
        <div className="space-y-4">
          <ErrorLine error={s.save.error} />
          <TextField
            label={t('prereg.members.archiveAfter')}
            hint={t('prereg.members.zeroOff')}
            type="number"
            min={0}
            max={3650}
            disabled={!canEdit}
            value={d.lifecycle.archiveInactiveAfterDays}
            onChange={(e) => setLife({ archiveInactiveAfterDays: days(e.target.value) })}
            className="max-w-xs"
          />
          <TextField
            label={t('prereg.members.deleteAfter')}
            hint={t('prereg.members.zeroOff')}
            type="number"
            min={0}
            max={3650}
            disabled={!canEdit}
            value={d.lifecycle.deleteArchivedAfterDays}
            onChange={(e) => setLife({ deleteArchivedAfterDays: days(e.target.value) })}
            className="max-w-xs"
          />
        </div>
      </Card>
      <Card title={t('prereg.onboarding.title')} description={t('prereg.onboarding.description')} footer={footer}>
        <div className="space-y-4">
          <CheckboxField label={t('prereg.onboarding.enabled')} disabled={!canEdit} checked={ob.enabled} onChange={(e) => set({ onboarding: { ...ob, enabled: e.target.checked } })} />
          {ob.enabled ? (
            <>
              <SelectField label={t('prereg.onboarding.sendOn')} disabled={!canEdit} value={ob.sendOn} onChange={(e) => set({ onboarding: { ...ob, sendOn: e.target.value as typeof ob.sendOn } })}>
                <option value="created">{t('prereg.onboarding.sendOn.created')}</option>
                <option value="start_date">{t('prereg.onboarding.sendOn.start_date')}</option>
              </SelectField>
              <CheckboxField
                label={t('prereg.onboarding.companion')}
                hint={t('prereg.onboarding.companionHint')}
                disabled={!canEdit}
                checked={ob.includeCompanionInvite}
                onChange={(e) => set({ onboarding: { ...ob, includeCompanionInvite: e.target.checked } })}
              />
              <EmailSection
                value={ob}
                onChange={(onboarding) => set({ onboarding })}
                context="onboarding"
                uploadPath="/members/onboarding-attachment"
                groupId={scopeId}
                showCalendar={false}
                disabled={!canEdit}
              />
              {emailError(ob.replyTo) ? <p className="text-sm text-red-700">{emailError(ob.replyTo)}</p> : null}
            </>
          ) : null}
        </div>
      </Card>
    </div>
  );
}

/** Registered for `members`: a repeat group's lifecycle rules (kept from Phase 1) and the new onboarding email. */
export function MembersSettingsCard({ scope, scopeId, moduleKey, canEdit }: SettingsCardProps) {
  if (scope !== 'groups') return <SettingsEditor moduleKey={moduleKey} base={`/${scope}/${scopeId}/settings`} canEdit={canEdit} />;
  return <MembersGroupCard scopeId={scopeId} canEdit={canEdit} />;
}
